import type {AuditAdjustLine} from '@/services/auditService/interface/adjust-orchestrate';
import type {FsCategoryLine} from '@/store/audit/fsCategoryStore';
import {resolvePayloadStrategy} from '@/pages/components/audit/adjustment/adapters/strategies';
import {
    CommonRow, BankRow, CommonDebtRow, CommonFeesRow, TestRow,
    RelatedRow, PrincipalSubsidiaryRow, PPERate, PPERow, TaxiCabsRow,
    DisposalRow, BankLoanRow, CommissionPaymentRow,
    RentalRow, VehicleLoanRow,
    StaffSalaryRow, DirectorSalaryRow, DirectorQuarterRow, DueFromToDirRow,
    IntangibleRow,
} from "@/services/auditService/interface/wp-row";

function strategyForLine(code: string, categoryLine: FsCategoryLine | null | undefined) {
    const templateCodeRef = String(categoryLine?.templateCodeRef || '').trim();
    const isCustomNode = categoryLine?.isCustom === true || categoryLine?.customNodeId != null;
    return resolvePayloadStrategy([
        code,
        templateCodeRef,
        isCustomNode ? 'CommonTransactionWp' : '',
    ]);
}

export interface BuildDetailsPayloadParams {
    code: string;
    rows?: CommonRow[];
    commonRow?: CommonRow[];  // 别名：与 rows 功能相同，支持 unifiedMergeData 传入
    categoryLine: FsCategoryLine | null;
    statement: 'BS' | 'PL';
    scheduleName?: string | null;
    companyId: number;
    annualId: number;
    totalFinal: number;
    testRows?: TestRow[];  // 可选：transaction 数据 (testRow or principalSubsidiaryRow)
    testRow?: TestRow[];  // 别名：与 testRows 功能相同，支持 unifiedMergeData 传入
    cutOffThisYearTest?: TestRow[];  // 可选：cutOffThisYear 数据（用于 PurchasesWorkingPaper, SalesIncomeWorkingPaper）
    cutOffNextYearTest?: TestRow[];  // 可选：cutOffNextYear 数据（用于 PurchasesWorkingPaper, SalesIncomeWorkingPaper）
    relatedRows?: RelatedRow[];     // 可选：related party transactions 数据
    bankRows?: BankRow[];     // 可选：bank 数据（用于 BankWorkingPaper）
    debtRows?: CommonDebtRow[];  // 可选：debt 数据（用于 DebtTransactionWorkingPaper）
    feesRows?: CommonFeesRow[];  // 可选：fees 数据（用于 FeesTransactionWorkingPaper）
    principalSubsidiaryRows?: PrincipalSubsidiaryRow[];  // 可选：principalSubsidiaryRow 数据（用于 InvestmentSubsidiaryWorkingPaper）
    costRows?: CommonRow[];  // 可选：costRow 数据（用于 InvestmentSubsidiaryWorkingPaper）
    impairmentLossRows?: CommonRow[];  // 可选：impairmentLossRow 数据（用于 InvestmentSubsidiaryWorkingPaper）
    dueFromToDirectorsRows?: CommonRow[];  // legacy
    dueFromToDirRow?: DueFromToDirRow[];
    gainExchangeRows?: CommonRow[];  // 可选：gainExchange 数据（用于 GainLossExchangeWorkingPaper）
    lossExchangeRows?: CommonRow[];  // 可选：lossExchange 数据（用于 GainLossExchangeWorkingPaper）
    gainDisposalRows?: CommonRow[];  // 可选：gainDisposal 数据（用于 GainLossDisposalWorkingPaper）
    lossDisposalRows?: CommonRow[];  // 可选：lossDisposal 数据（用于 GainLossDisposalWorkingPaper）
    commissionPaymentRows?: CommissionPaymentRow[];  // 可选：commissionPayments 数据（用于 CommissionPaymentWorkingPaper）
    taxDeductibleRows?: CommonRow[];  // 可选：taxDeductible 数据（用于 DonationWorkingPaper）
    nonTaxDeductibleRows?: CommonRow[];  // 可选：nonTaxDeductible 数据（用于 DonationWorkingPaper）
    // PPE 数据（用于 PropertyPlantEquipmentWorkingPaper）
    locationOfPropertyRow?: PPERow[];
    commercialAllowanceRow?: PPERow[];
    industrialAllowanceRow?: PPERow[];
    furnitureFixtureRow?: PPERow[];
    computerHardSoftRow?: PPERow[];
    fullPayMotorRow?: PPERow[];
    hirePurchaseMotorRow?: PPERow[];
    machineryRow?: PPERow[];
    liftEscalatorRow?: PPERow[];
    equipmentRow?: PPERow[];
    ppeRate?:PPERate;
    // Intangible Assets 数据（用于 IntangibleAssetsWorkingPaper）
    intangibleRows?: IntangibleRow[];
    /** @deprecated use intangibleRows */
    intangibleCostRows?: any[];
    /** @deprecated use intangibleRows */
    intangibleAccumulatedRows?: any[];
    /** @deprecated use intangibleRows */
    intangibleCols?: string[];
    // Investment In Taxi Cabs And Licenses 数据（用于 InvestmentInTaxiCabsAndLicensesWorkingPaper）
    taxiCabsCostRows?: TaxiCabsRow[];
    taxiCabsAccumulationRows?: TaxiCabsRow[];
    // Interest Income 数据（用于 InterestIncomeWorkingPaper）
    bankInterestRows?: any[];
    otherInterestRows?: any[];
    // Salaries Allowance 数据（用于 ISalariesAllowance）- New structure (2025/11/08)
    staffRow?: StaffSalaryRow[];
    workerRow?: StaffSalaryRow[];
    directorRow?: DirectorSalaryRow[];
    directorQuarterRow?: DirectorQuarterRow[];
    // Bank Loan 数据（用于 BankLoanWorkingPaper）
    bankLoans?: BankLoanRow[];
    vehicleLoans?: VehicleLoanRow[];
    loans?: any[]; // Generic loans parameter for backward compatibility
    // Rental Expenses 数据（用于 RentalExpensesWorkingPaper）
    rentalRows?: RentalRow[];
    monthsForPeriod?: string | null;
    extractPL?: string;
    deletedMonthsByCol?: Record<string, string[]>;
    rentalTotalsByCol?: Record<string, number>;
    // Gain/Loss Disposal 数据（用于 GainLossDisposalWorkingPaper）
    subsidiaryRow?: DisposalRow[];
    associateRow?: DisposalRow[];
    intangibleRow?: DisposalRow[];
    taxiLicenseDisRow?: DisposalRow[];
    fullPayTaxiCabDisRow?: DisposalRow[];
    hirePurchaseTaxiCabDisRow?: DisposalRow[];
    trustsRow?: DisposalRow[];
    // Annual Info 数据（通用，可用于多个 Working Paper）
    annualInfo?: any;
    // PPE Disposal rows (DisposalRow type for Gain/Loss Disposal)
    locationOfPropertyDisRow?: DisposalRow[];
    commercialAllowanceDisRow?: DisposalRow[];
    industrialAllowanceDisRow?: DisposalRow[];
    furnitureFixtureDisRow?: DisposalRow[];
    computerHardSoftDisRow?: DisposalRow[];
    fullPayMotorDisRow?: DisposalRow[];
    hirePurchaseMotorDisRow?: DisposalRow[];
    machineryDisRow?: DisposalRow[];
    liftEscalatorDisRow?: DisposalRow[];
    equipmentDisRow?: DisposalRow[];
}

export interface BuildAdjustmentLineParams {
    code: string;
    detailsPayload: any;
    categoryLine: FsCategoryLine;
    companyId: number;
    annualId: number;
    statement: 'BS' | 'PL';
    currency: string;
    totalAdjustment: number;
    totalFinal: number;
    lineId?: string;  // 可选：从后端获取的 line id（UUID），用于更新现有数据
    entry?: string;   // Dr./Cr. - 根据 adjustmentAmount 调整后的值
}

/**
 * 構建 detailsPayload - 根據 code 自動切換對應的 strategy
 * @param params - 構建參數
 * @returns detailsPayload 對象
 */
export const buildDetailsPayload = (params: BuildDetailsPayloadParams): any => {
    const {
        code,
        rows,
        commonRow,  // 支持别名
        categoryLine,
        statement,
        scheduleName,
        companyId,
        annualId,
        totalFinal,
        testRows,
        testRow,  // 支持别名
        cutOffThisYearTest,
        cutOffNextYearTest,
        relatedRows,
        bankRows,
        debtRows,
        feesRows,
        principalSubsidiaryRows,
        costRows,
        impairmentLossRows,
        dueFromToDirectorsRows,
        dueFromToDirRow,
        gainExchangeRows,
        lossExchangeRows,
        gainDisposalRows,
        lossDisposalRows,
        commissionPaymentRows,
        taxDeductibleRows,
        nonTaxDeductibleRows,
        locationOfPropertyRow,
        commercialAllowanceRow,
        industrialAllowanceRow,
        furnitureFixtureRow,
        computerHardSoftRow,
        fullPayMotorRow,
        hirePurchaseMotorRow,
        machineryRow,
        liftEscalatorRow,
        equipmentRow,
        ppeRate,
        intangibleRows,
        intangibleCostRows,
        intangibleAccumulatedRows,
        intangibleCols,
        taxiCabsCostRows,
        taxiCabsAccumulationRows,
        bankInterestRows,
        otherInterestRows,
        // Salaries Allowance fields
        staffRow,
        workerRow,
        directorRow,
        directorQuarterRow,
        bankLoans,
        vehicleLoans,
        loans,
        rentalRows,
        monthsForPeriod,
        extractPL,
        deletedMonthsByCol,
        rentalTotalsByCol,
        subsidiaryRow,
        associateRow,
        intangibleRow,
        taxiLicenseDisRow,
        fullPayTaxiCabDisRow,
        hirePurchaseTaxiCabDisRow,
        trustsRow,
        locationOfPropertyDisRow,
        commercialAllowanceDisRow,
        industrialAllowanceDisRow,
        furnitureFixtureDisRow,
        computerHardSoftDisRow,
        fullPayMotorDisRow,
        hirePurchaseMotorDisRow,
        machineryDisRow,
        liftEscalatorDisRow,
        equipmentDisRow,
    } = params;

    const strategy = strategyForLine(code, categoryLine);
    
    if (!strategy) {
        console.warn(`No strategy found for code: ${code}`);
        return {component: '', payload: params};
    }
    
    // Simply return the strategy's default payload for now
    // The complete implementation is in buildDetailsPayloadComplete
    // 支持别名：rows/commonRow 和 testRows/testRow
    const effectiveRows = rows || commonRow || [];
    const effectiveTestRows = testRows || testRow;
    
    const effectiveDueFromToDirRow = dueFromToDirRow || commonRowsToDueFromToDirRows(dueFromToDirectorsRows);

    return strategy.buildPayload({
        prev: {
            commonRow: effectiveRows,
            testRow: effectiveTestRows,
            cutOffThisYearTest,
            cutOffNextYearTest,
            relatedRows,
            bankRows,
            debtRows,
            feesRows,
            principalSubsidiaryRows,
            costRows,
            impairmentLossRows,
            dueFromToDirectorsRows,
            dueFromToDirRow: effectiveDueFromToDirRow,
            gainExchangeRows,
            lossExchangeRows,
            gainDisposalRows,
            lossDisposalRows,
            commissionPaymentRows,
            taxDeductibleRows,
            nonTaxDeductibleRows,
            locationOfPropertyRow,
            commercialAllowanceRow,
            industrialAllowanceRow,
            furnitureFixtureRow,
            computerHardSoftRow,
            fullPayMotorRow,
            hirePurchaseMotorRow,
            machineryRow,
            liftEscalatorRow,
            equipmentRow,
            locationOfPropertyDisRow,
            commercialAllowanceDisRow,
            industrialAllowanceDisRow,
            furnitureFixtureDisRow,
            computerHardSoftDisRow,
            fullPayMotorDisRow,
            hirePurchaseMotorDisRow,
            machineryDisRow,
            liftEscalatorDisRow,
            equipmentDisRow,
            ppeRate,
            intangibleRows,
            intangibleCostRows,
            intangibleAccumulatedRows,
            intangibleCols,
            taxiCabsCostRows,
            taxiCabsAccumulationRows,
            bankLoans,
            vehicleLoans,
            // RentalExpense fields
            monthsForPeriod: monthsForPeriod,
            extractPL: extractPL,
            deletedMonthsByCol: deletedMonthsByCol,
            rentalTotalsByCol: rentalTotalsByCol,
            rentalRows: rentalRows,
            // Salaries Allowance fields
            staffRow,
            workerRow,
            directorRow,
            directorQuarterRow,
            annualInfo: params.annualInfo,
        },
        ctx: {
            item: categoryLine,
            statement: statement,
            code: code,  // Use code instead of scheduleCode
            scheduleName: scheduleName,
            companyId: companyId,
            annualId: annualId,
        },
        amount: totalFinal,
    });
};


// Helper functions
const strHas = (v: any): boolean => {
    return typeof v === 'string' && v.trim().length > 0;
};

const numHas = (v: any): boolean => {
    return v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
};

const filterCommonRows = (arr?: CommonRow[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.filter((r) =>
        strHas(r.item) ||
        numHas(r.perClient) ||
        (Array.isArray(r.adjustments) && r.adjustments.length > 0) ||
        numHas(r.lastYearFinal)
    );
};

const commonRowsToDueFromToDirRows = (arr?: CommonRow[]): DueFromToDirRow[] | undefined => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.map((r) => ({
        id: r.id,
        annualId: r.annualId,
        code: r.code,
        directorName: r.item,
        perClient: r.perClient ?? undefined,
        adjustments: r.adjustments ?? undefined,
        lastYearFinal: r.lastYearFinal ?? undefined,
    }));
};

const filterCommissionRows = (arr?: CommissionPaymentRow[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.filter((r) =>
        strHas((r as any).commissionPaymentName) ||
        numHas(r.perClient) ||
        (Array.isArray(r.adjustments) && r.adjustments.length > 0) ||
        numHas(r.lastYearFinal)
    );
};

const filterBankRows = (arr?: BankRow[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.filter((r) =>
        numHas(r.bankId) ||
        numHas(r.perClient) ||
        (Array.isArray(r.adjustments) && r.adjustments.length > 0) ||
        numHas(r.lastYearFinal)
    );
};

const filterDebtRows = (arr?: CommonDebtRow[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.filter((r) =>
        strHas((r as any).debtName) ||
        numHas(r.perClient) ||
        (Array.isArray(r.adjustments) && r.adjustments.length > 0) ||
        numHas(r.lastYearFinal)
    );
};

const filterFeesRows = (arr?: CommonFeesRow[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.filter((r) =>
        strHas((r as any).feesName) ||
        numHas(r.perClient) ||
        (Array.isArray(r.adjustments) && r.adjustments.length > 0) ||
        numHas(r.lastYearFinal)
    );
};

const toNum = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const n = Number(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : null;
};

// Helper: normalize id field for any row array
const normalizeIdList = <T extends Record<string, any>>(arr?: T[]) => {
    if (!Array.isArray(arr)) return arr as any;
    return arr.map((item) => {
        const copy: any = {...item};
        // 保留后端返回的有效 id (id > 0)
        // 对于新建的数据（id <= 0 或 undefined），从对象中移除 id 字段
        // 注意：保留所有正整数 id，包括从后端获取的数据
        if (copy.id !== undefined && copy.id !== null) {
            const numId = Number(copy.id);
            if (!Number.isFinite(numId) || numId <= 0) {
                delete copy.id;
            }
            // 如果 id > 0，保留它（不删除）
        } else {
            // id 为 undefined 或 null，删除该字段
            delete copy.id;
        }
        return copy as T;
    }) as T[];
};


/**
 * 構建 detailsPayload - 根據 code 自動切換對應的 strategy（補充版）
 * 這個版本包含了所有需要的數據處理邏輯
 */
export const buildDetailsPayloadComplete = (params: BuildDetailsPayloadParams): any => {
    const {
        code,
        rows,
        categoryLine,
        testRows,
        cutOffThisYearTest,
        cutOffNextYearTest,
        relatedRows,
        debtRows,
        feesRows,
        principalSubsidiaryRows,
        costRows,
        impairmentLossRows,
        dueFromToDirectorsRows,
        dueFromToDirRow, // Fix: ensure dueFromToDirRow is in scope
        gainExchangeRows,
        lossExchangeRows,
        gainDisposalRows,
        lossDisposalRows,
        commissionPaymentRows,
        taxDeductibleRows,
        nonTaxDeductibleRows,
        bankRows,
        locationOfPropertyRow,
        commercialAllowanceRow,
        industrialAllowanceRow,
        furnitureFixtureRow,
        computerHardSoftRow,
        fullPayMotorRow,
        hirePurchaseMotorRow,
        machineryRow,
        liftEscalatorRow,
        equipmentRow,
        intangibleRows,
        intangibleCostRows,
        intangibleAccumulatedRows,
        intangibleCols,
        taxiCabsCostRows,
        taxiCabsAccumulationRows,
        staffRow,
        workerRow,
        directorRow,
        directorQuarterRow,
        bankLoans,
        vehicleLoans,
        loans,
        rentalRows,
        monthsForPeriod,
        extractPL,
        deletedMonthsByCol,
        rentalTotalsByCol,
        subsidiaryRow,
        associateRow,
        intangibleRow,
        taxiLicenseDisRow,
        fullPayTaxiCabDisRow,
        hirePurchaseTaxiCabDisRow,
        trustsRow,
        locationOfPropertyDisRow,
        commercialAllowanceDisRow,
        industrialAllowanceDisRow,
        furnitureFixtureDisRow,
        computerHardSoftDisRow,
        fullPayMotorDisRow,
        hirePurchaseMotorDisRow,
        machineryDisRow,
        liftEscalatorDisRow,
        equipmentDisRow,
        ppeRate,
    } = params;
    
    const strategy = strategyForLine(code, categoryLine);
    
    if (!strategy) {
        console.warn(`No strategy found for code: ${code}`);
        return {component: '', payload: params};
    }
    
    // Prepare normalized arrays
    const nRows = normalizeIdList(filterCommonRows(rows));
    const nTestRows = normalizeIdList(testRows);
    const nCutOffThis = normalizeIdList(cutOffThisYearTest);
    const nCutOffNext = normalizeIdList(cutOffNextYearTest);
    const nRelatedRows = normalizeIdList(relatedRows);
    const nDebtRows = normalizeIdList(filterDebtRows(debtRows));
    const nFeesRows = normalizeIdList(filterFeesRows(feesRows));
    const nPrincipalSubsidiaryRows = normalizeIdList(principalSubsidiaryRows);
    const nCostRows = normalizeIdList(filterCommonRows(costRows));
    const nImpairmentLossRows = normalizeIdList(filterCommonRows(impairmentLossRows));
    const nDueFromToDirRow = normalizeIdList(dueFromToDirRow || commonRowsToDueFromToDirRows(dueFromToDirectorsRows));
    const nGainExchangeRows = normalizeIdList(filterCommonRows(gainExchangeRows));
    const nLossExchangeRows = normalizeIdList(filterCommonRows(lossExchangeRows));
    const nGainDisposalRows = normalizeIdList(filterCommonRows(gainDisposalRows));
    const nLossDisposalRows = normalizeIdList(filterCommonRows(lossDisposalRows));
    const nCommissionPaymentRows = normalizeIdList(filterCommissionRows(commissionPaymentRows));
    const nTaxDeductibleRows = normalizeIdList(filterCommonRows(taxDeductibleRows));
    const nNonTaxDeductibleRows = normalizeIdList(filterCommonRows(nonTaxDeductibleRows));
    const fBankRows = filterBankRows(bankRows);
    const nLocationOfPropertyRow = normalizeIdList(locationOfPropertyRow);
    const nCommercialAllowanceRow = normalizeIdList(commercialAllowanceRow);
    const nIndustrialAllowanceRow = normalizeIdList(industrialAllowanceRow);
    const nFurnitureFixtureRow = normalizeIdList(furnitureFixtureRow);
    const nComputerHardSoftRow = normalizeIdList(computerHardSoftRow);
    const nFullPayMotorRow = normalizeIdList(fullPayMotorRow);
    const nHirePurchaseMotorRow = normalizeIdList(hirePurchaseMotorRow);
    const nMachineryRow = normalizeIdList(machineryRow);
    const nLiftEscalatorRow = normalizeIdList(liftEscalatorRow);
    const nEquipmentRow = normalizeIdList(equipmentRow);
    const nIntangibleRows = normalizeIdList(
        (intangibleRows ?? intangibleCostRows) as any
    );
    const nTaxiCabsCostRows = normalizeIdList(taxiCabsCostRows);
    const nTaxiCabsAccumulationRows = normalizeIdList(taxiCabsAccumulationRows);
    const nStaffRow = normalizeIdList(staffRow);
    const nWorkerRow = normalizeIdList(workerRow);
    const nDirectorRow = normalizeIdList(directorRow);
    const nDirectorQuarterRow = normalizeIdList(directorQuarterRow);
    const nSubsidiaryRow = normalizeIdList(subsidiaryRow);
    const nAssociateRow = normalizeIdList(associateRow);
    const nIntangibleRow = normalizeIdList(intangibleRow);
    const nTaxiLicenseDisRow = normalizeIdList(taxiLicenseDisRow);
    const nFullPayTaxiCabDisRow = normalizeIdList(fullPayTaxiCabDisRow);
    const nHirePurchaseTaxiCabDisRow = normalizeIdList(hirePurchaseTaxiCabDisRow);
    const nTrustsRow = normalizeIdList(trustsRow);
    const nLocationOfPropertyDisRow = normalizeIdList(locationOfPropertyDisRow);
    const nCommercialAllowanceDisRow = normalizeIdList(commercialAllowanceDisRow);
    const nIndustrialAllowanceDisRow = normalizeIdList(industrialAllowanceDisRow);
    const nFurnitureFixtureDisRow = normalizeIdList(furnitureFixtureDisRow);
    const nComputerHardSoftDisRow = normalizeIdList(computerHardSoftDisRow);
    const nFullPayMotorDisRow = normalizeIdList(fullPayMotorDisRow);
    const nHirePurchaseMotorDisRow = normalizeIdList(hirePurchaseMotorDisRow);
    const nMachineryDisRow = normalizeIdList(machineryDisRow);
    const nLiftEscalatorDisRow = normalizeIdList(liftEscalatorDisRow);
    const nEquipmentDisRow = normalizeIdList(equipmentDisRow);

    // 使用對應的 strategy 構建 payload（使用規範化 ID 的數據）
    return strategy.buildPayload({
        prev: {
            commonRow: nRows,
            testRow: nTestRows,                              // 映射为 testRow
            transactionTest: nTestRows,                      // 映射为 transactionTest（用于 PurchasesWorkingPaper, SalesIncomeWorkingPaper）
            cutOffThisYearTest: nCutOffThis,                 // 映射为 cutOffThisYearTest（用于 PurchasesWorkingPaper, SalesIncomeWorkingPaper）
            cutOffNextYearTest: nCutOffNext,                 // 映射为 cutOffNextYearTest（用于 PurchasesWorkingPaper, SalesIncomeWorkingPaper）
            relatedRows: nRelatedRows,                       // 映射为 relatedRows
            bankRow: fBankRows,                                     // 映射为 bankRow（用于 BankWorkingPaper）
            debtRow: nDebtRows,                                     // 映射为 debtRow（用于 DebtTransactionWorkingPaper）
            feesRow: nFeesRows,                                     // 映射为 feesRow（用于 FeesTransactionWorkingPaper）
            principalSubsidiaryRow: nPrincipalSubsidiaryRows,       // 映射为 principalSubsidiaryRow（用于 InvestmentSubsidiaryWorkingPaper）
            costRow: nCostRows,                                     // 映射为 costRow（用于 InvestmentSubsidiaryWorkingPaper）
            impairmentLossRow: nImpairmentLossRows,                 // 映射为 impairmentLossRow（用于 InvestmentSubsidiaryWorkingPaper）
            dueFromToDirRow: nDueFromToDirRow,
            gainExchange: nGainExchangeRows,                        // 映射为 gainExchange（用于 GainLossExchangeWorkingPaper）
            lossExchange: nLossExchangeRows,                        // 映射为 lossExchange（用于 GainLossExchangeWorkingPaper）
            gainDisposal: nGainDisposalRows,                        // 映射为 gainDisposal（用于 GainLossDisposalWorkingPaper）
            lossDisposal: nLossDisposalRows,                        // 映射为 lossDisposal（用于 GainLossDisposalWorkingPaper）
            commissionPayments: nCommissionPaymentRows,             // 映射为 commissionPayments（用于 CommissionPaymentWorkingPaper）
            taxDeductible: nTaxDeductibleRows,                      // 映射为 taxDeductible（用于 DonationWorkingPaper）
            nonTaxDeductible: nNonTaxDeductibleRows,                // 映射为 nonTaxDeductible（用于 DonationWorkingPaper）
            relatedItems: nRelatedRows,                             // 映射为 relatedItems（通用）
            purchases: nRows,                                       // 映射为 purchases（用于 PurchasesWorkingPaper）
            salesIncome: nRows,                                     // 映射为 salesIncome（用于 SalesIncomeWorkingPaper）
            // PPE 数据映射
            locationOfPropertyRow: nLocationOfPropertyRow,
            commercialAllowanceRow: nCommercialAllowanceRow,
            industrialAllowanceRow: nIndustrialAllowanceRow,
            furnitureFixtureRow: nFurnitureFixtureRow,
            computerHardSoftRow: nComputerHardSoftRow,
            fullPayMotorRow: nFullPayMotorRow,
            hirePurchaseMotorRow: nHirePurchaseMotorRow,
            machineryRow: nMachineryRow,
            liftEscalatorRow: nLiftEscalatorRow,
            equipmentRow: nEquipmentRow,
            ppeRate:ppeRate,
            // Intangible Assets 数据映射
            intangibleRows: nIntangibleRows,
            // Investment In Taxi Cabs And Licenses 数据映射
            taxiCabsCostRows: nTaxiCabsCostRows,
            taxiCabsAccumulationRows: nTaxiCabsAccumulationRows,
            // Interest Income 数据映射
            bankInterestIncome: params.bankInterestRows,
            otherInterestIncome: params.otherInterestRows,
            // Bank Loan 数据映射
            bankLoans: bankLoans,
            vehicleLoans: vehicleLoans,
            loan: loans, // Generic loans mapping
            // Rental Expenses 数据映射
            rows: normalizeIdList(rentalRows),
            monthsForPeriod: monthsForPeriod,
            extractPL: extractPL,
            deletedMonthsByCol: deletedMonthsByCol,
            rentalTotalsByCol: rentalTotalsByCol,
            // Salaries Allowance 数据映射
            staffRow: nStaffRow,
            workerRow: nWorkerRow,
            directorRow: nDirectorRow,
            directorQuarterRow: nDirectorQuarterRow,
            // Gain/Loss Disposal 数据映射
            subsidiaryRow: nSubsidiaryRow,
            associateRow: nAssociateRow,
            intangibleRow: nIntangibleRow,
            taxiLicenseDisRow: nTaxiLicenseDisRow,
            fullPayTaxiCabDisRow: nFullPayTaxiCabDisRow,
            hirePurchaseTaxiCabDisRow: nHirePurchaseTaxiCabDisRow,
            trustsRow: nTrustsRow,
            // PPE Disposal rows (DisposalRow type)
            locationOfPropertyDisRow: nLocationOfPropertyDisRow,
            commercialAllowanceDisRow: nCommercialAllowanceDisRow,
            industrialAllowanceDisRow: nIndustrialAllowanceDisRow,
            furnitureFixtureDisRow: nFurnitureFixtureDisRow,
            computerHardSoftDisRow: nComputerHardSoftDisRow,
            fullPayMotorDisRow: nFullPayMotorDisRow,
            hirePurchaseMotorDisRow: nHirePurchaseMotorDisRow,
            machineryDisRow: nMachineryDisRow,
            liftEscalatorDisRow: nLiftEscalatorDisRow,
            equipmentDisRow: nEquipmentDisRow,
            // Annual Info 数据
            annualInfo: params.annualInfo,
        },
        ctx: {
            item: params.categoryLine,
            statement: params.statement,
            code: code,
            scheduleName: params.scheduleName,
            companyId: params.companyId,
            annualId: params.annualId,
        },
        amount: params.totalFinal,
    });
};

/**
 * 構建 AuditAdjustmentLine 對象
 * @param params - 構建參數
 * @returns AuditAdjustmentLine 對象
 */
export const buildAdjustmentLine = (params: BuildAdjustmentLineParams): AuditAdjustLine => {
    const {
        code,
        detailsPayload,
        categoryLine,
        companyId,
        annualId,
        statement,
        currency,
        totalAdjustment,
        totalFinal,
        lineId,
        entry,
    } = params;

    const line: AuditAdjustLine = {
        fsId: categoryLine.fsId,
        code: code,
        currency: currency,
        adjustmentAmount: totalAdjustment,
        details: [detailsPayload],
        entry: entry,  // Dr./Cr. 根据 adjustmentAmount 调整后的值
    };
    
    // 如果提供了 lineId，添加到对象中（保留后端返回的 line id）
    if (lineId) {
        line.id = lineId;
    }
    
    // 返回 AuditAdjustLineVO，包含额外字段用于兼容性
    return {
        ...line,
        // 这些字段用于向后兼容
        companyId,
        annualId,
        statement,
        scheduleName: categoryLine?.scheduleName || '',
        openingAmount: totalFinal - totalAdjustment,
        closingAmount: totalFinal,
    } as AuditAdjustLine & Record<string, any>;
};